import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { filter, Observable, pipe } from 'rxjs';
import { LoanPayload, LoanPostStatusResponse, LoanSteps, NewLoansService } from './new-loans.service';
import { NewSavingsAccountService } from '@brightside-web/desktop/feature/savings-account';
import { take } from 'rxjs/operators';

@Injectable()
export class LoanInfoGuard implements CanActivate {
  constructor(
    private newLoanSvc: NewLoansService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private newSavingsAcctSvc: NewSavingsAccountService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return new Observable<boolean>((obs) => {
      this.newLoanSvc
        .getLoanByPartnerType()
        .pipe(take(1))
        .subscribe((loanResponse) => {
          if (typeof loanResponse === 'boolean' && !loanResponse) {
            console.warn('No loan vendor in cache');
            this.router.navigate(['home']);
            obs.next(false);
          } else {
            const loanInfo = loanResponse as LoanPayload;
            this.newSavingsAcctSvc.getSavingsAccounts(true);
            this.newSavingsAcctSvc.currentAccountInfo
              .pipe(
                filter((savingsAccount) => savingsAccount !== undefined),
                take(1)
              )
              .subscribe((savingsAccount) => {
                if (savingsAccount) {
                  this.newLoanSvc.hasSavings = true;
                  if (savingsAccount.fundsSource?.autoSaveSchedule.status !== 'active') {
                    this.newLoanSvc.hasAutosave = false;
                  }
                }
                if (loanInfo.payload.loans.length > 0 && this.hasInProgressLoan(loanInfo.payload.loans)) {
                  if (savingsAccount?.fundsSource?.autoSaveSchedule.status === 'active') {
                    this.newLoanSvc
                      .doesLoanHaveDocs()
                      .pipe(take(1))
                      .subscribe((docKey) => {
                        if (!docKey) {
                          this.newLoanSvc.setStep(LoanSteps.INFO);
                          obs.next(true);
                        } else {
                          if (typeof docKey === 'string') {
                            this.router.navigate(['accounts', 'loans', 'legal'], { state: { documentsKey: docKey } });
                            obs.next(false);
                          } else {
                            this.router.navigate(['home']);
                            obs.next(false);
                          }
                        }
                      });
                  } else {
                    this.newLoanSvc.setStep(LoanSteps.AUTOSAVE);
                    this.router.navigate(['accounts', 'loans', 'creation', 'intro']);
                    obs.next(false);
                  }
                } else {
                  this.router.navigate(['accounts', 'loans', 'creation', 'intro']);
                  this.newLoanSvc.setStep(LoanSteps.INTRO);
                  obs.next(false);
                }
              });
          }
        });
    }).pipe(take(1));
  }

  hasInProgressLoan(loans: LoanPostStatusResponse[]): boolean {
    let hasInProgress = false;
    for (let i = 0; i < loans.length; i++) {
      if (loans[i].inProgress && !loans[i].isTaken) {
        hasInProgress = true;
        break;
      }
    }
    return hasInProgress;
  }
}
